
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { Calendar, ShoppingCart, Stethoscope, MessageSquare } from "lucide-react";

export const DashboardStats = () => {
  const isMobile = useIsMobile();
  
  const { data: stats, isLoading } = useQuery({
    queryKey: ["professional-dashboard-stats"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const [appointmentsResult, servicesResult, messagesResult, purchaseRequestsResult] = await Promise.all([
        supabase
          .from("professional_appointments")
          .select("*")
          .eq("professional_id", user.id),
        supabase
          .from("professional_services")
          .select("*")
          .eq("professional_id", user.id),
        supabase
          .from("messages")
          .select("*")
          .eq("receiver_id", user.id),
        supabase
          .from("product_purchase_requests")
          .select("*")
          .eq("professional_id", user.id)
          .eq("status", "approved"),
      ]);

      return {
        totalPurchases: purchaseRequestsResult.data?.length || 0,
        totalAppointments: appointmentsResult.data?.length || 0,
        totalServices: servicesResult.data?.length || 0,
        totalMessages: messagesResult.data?.length || 0,
      };
    },
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {[1, 2, 3, 4].map((n) => (
          <Card key={n} className="animate-pulse">
            <CardContent className="h-[100px]" />
          </Card>
        ))}
      </div>
    );
  }

  const statItems = [
    { 
      title: "Achats", 
      value: stats?.totalPurchases || 0, 
      color: "bg-pink-50 hover:bg-pink-100 dark:bg-pink-900/10 dark:hover:bg-pink-900/20",
      icon: ShoppingCart,
    },
    { 
      title: "Rendez-vous", 
      value: stats?.totalAppointments || 0, 
      color: "bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/10 dark:hover:bg-blue-900/20",
      icon: Calendar,
    },
    { 
      title: "Services", 
      value: stats?.totalServices || 0, 
      color: "bg-green-50 hover:bg-green-100 dark:bg-green-900/10 dark:hover:bg-green-900/20",
      icon: Stethoscope,
    },
    { 
      title: "Messages", 
      value: stats?.totalMessages || 0, 
      color: "bg-purple-50 hover:bg-purple-100 dark:bg-purple-900/10 dark:hover:bg-purple-900/20",
      icon: MessageSquare,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      {statItems.map((item) => {
        const Icon = item.icon;
        return (
          <Card 
            key={item.title} 
            className={`transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg ${item.color}`}
          >
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {item.title}
              </CardTitle>
              <Icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{item.value}</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
